import { parseISOToDateTime, diff } from '@calendly/datetime'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Logger } from '@calendly/logging-service'
import { useMedia, usePrevious } from '@calendly/hooks'
import { MainPanel } from '@calendly/booking-kit'
import { parseQuery } from '@calendly/http-tools'
import { useFlag } from '@calendly/feature-check-react'

import Container from 'components/layout/container'
import { MutualAvailability } from 'components/mutual_availability'
import {
  isPollSelector
} from 'selectors/event_type'
import { setEventType, eventTypeSelector } from 'root-api'
import {
  timezoneSelector,
  monthSelector,
  eventSelector
} from 'selectors'
import { calAnonymousIdSelector } from 'selectors/user'
import {
  daySelector,
  hideEventTypeDetailsSelector
} from 'selectors/calendar'
import {
  clearCalendar,
  fetchCalendar
} from 'actions/calendar'
import { setBookingMethod } from 'actions/spot'
import { preselectCalendarDay } from 'actions/calendar_day'
import GoogleAnalytics from 'services/analytics/google'
import EmbedAnalytics from 'services/analytics/embed'
import FbPixel from 'services/analytics/facebook_pixel'
import { useStatsigLayer } from 'hooks/use-statsig-layer'
import { useStatsigExperimentGroupName } from 'hooks/use-statsig-experiment-group-name'

import Sidebar from './sidebar'
import CalendarError from './calendar_error'
import Spotpicker from './spotpicker'
import FlatSpotpicker from './flat_spotpicker'

const LARGE_SCREEN_MEDIA_QUERY = { minWidth: 1260 }

const SelectDateTime = () => {
  const dispatch = useDispatch()
  const { search } = useLocation()
  const eventType = useSelector(eventTypeSelector)
  const day = useSelector(daySelector)
  const month = useSelector(monthSelector)
  const isPoll = useSelector(isPollSelector)
  const timezone = useSelector(timezoneSelector)
  const shouldHideSidebar = useSelector(hideEventTypeDetailsSelector)
  const event = useSelector(eventSelector)
  const isMutualAvailabilityEnabled = useStatsigLayer('booking_flow').get('mutual_availability_sign_up_with_google', false)
  const bookingAppRedesign = useFlag('booking_app_redesign', false)
  const aaExperimentVariant = useStatsigExperimentGroupName('growth_booking_marketing_aa_test')
  const calAnonymousId = useSelector(calAnonymousIdSelector)
  const hasBeenExposedToAAExperiment = useRef(false)

  const isLargeScreen = useMedia(LARGE_SCREEN_MEDIA_QUERY, false)

  const durationParam = useMemo(() => {
    const durationQuery = parseQuery(search).duration
    return durationQuery ? Number(durationQuery) : undefined
  }, [search])

  const duration = eventType.durationOverride || durationParam

  const showFlatSpotpicker = useMemo(() => eventType.spotpicker_type === 'FlatSpotpicker',
    [eventType.spotpicker_type]
  )

  const isExpanded = useMemo(() => !!day || showFlatSpotpicker,
    [day, showFlatSpotpicker]
  )

  const { name: eventTypeName, duration_options: durationOptions } = useMemo(() => ({
    name: eventType.name,
    duration_options: eventType.duration_options
  }), [eventType.name, eventType.duration_options])

  const isDurationOptionIncluded = useCallback((option) => durationOptions?.includes(option), [durationOptions])

  const eventDuration = useMemo(() => {
    if (!event) return undefined
    return diff(parseISOToDateTime(event.end_time), parseISOToDateTime(event.start_time), 'minutes')
  }, [event])

  useEffect(() => {
    if (hasBeenExposedToAAExperiment.current || !aaExperimentVariant || !calAnonymousId) {
      return
    }

    hasBeenExposedToAAExperiment.current = true
  }, [aaExperimentVariant, calAnonymousId])

  useEffect(() => {
    if (eventDuration !== eventType.duration && isDurationOptionIncluded(eventDuration)) {
      dispatch(setEventType({ duration: eventDuration, durationOverride: eventDuration }))
    }
  }, [eventDuration, eventType.duration, isDurationOptionIncluded, dispatch])

  useEffect(() => {
    if (isDurationOptionIncluded(duration)) {
      dispatch(setEventType({ duration, durationOverride: duration }))
    }
  }, [isDurationOptionIncluded, duration, dispatch])

  useEffect(() => {
    dispatch(preselectCalendarDay(search))
  }, [dispatch, search])

  const prevTimezone = usePrevious(timezone)
  const prevDuration = usePrevious(duration)
  const prevEventTypeName = usePrevious(eventTypeName)

  useEffect(() => {
    if (prevEventTypeName !== undefined && prevEventTypeName !== eventTypeName) {
      dispatch(clearCalendar())
    }
    GoogleAnalytics.userTrackEvent('invitee_event_type_page', eventTypeName)
    FbPixel.trackCustom('invitee_event_type_page', { event_type_name: eventTypeName })
    EmbedAnalytics.track('event_type_viewed')
  }, [dispatch, eventTypeName, prevEventTypeName])

  useEffect(() => {
    if (prevTimezone !== undefined && prevTimezone !== timezone) {
      dispatch(clearCalendar())
    }
  }, [dispatch, timezone, prevTimezone])

  useEffect(() => {
    if (prevDuration !== duration) {
      dispatch(clearCalendar())
    }
  }, [dispatch, duration, prevDuration])

  useEffect(() => {
    if (!duration && isDurationOptionIncluded(eventDuration)) {
      dispatch(fetchCalendar(month, eventDuration))
    } else {
      dispatch(fetchCalendar(month, duration))
    }
  }, [dispatch, month, timezone, duration, isDurationOptionIncluded, eventDuration])

  useEffect(() => {
    if (day && !isPoll) {
      dispatch(fetchCalendar(day.date, duration))
    }
  }, [dispatch, day, timezone, duration, isPoll])

  useEffect(() => {
    dispatch(setBookingMethod(eventType.booking_method))
  }, [dispatch, eventType.booking_method])

  useEffect(() => {
    try {
      if (window.localStorage.getItem('publisherEmail')) {
        window.localStorage.removeItem('publisherEmail')
      }
    } catch (err) {
      // intentionally do nothing here, but we don't want
      // to break the booking app completely
      Logger.warn('Error accessing localStorage in a private window.', err)
    }
  }, [])

  return (
    <>
      <Container
        expanded={isExpanded}
        hiddenSidebar={shouldHideSidebar}
        hideBranding={bookingAppRedesign}
      >
        <Sidebar showFlatSpotpicker={showFlatSpotpicker} />
        <MainPanel>
          {showFlatSpotpicker ? <FlatSpotpicker /> : <Spotpicker />}
        </MainPanel>
        <CalendarError />
      </Container>
      {(isMutualAvailabilityEnabled || day) && isLargeScreen && <MutualAvailability />}
    </>
  )
}

export default SelectDateTime
